import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  TimePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import {
  useCompleteCrmFollowUp,
  useCrmActivities,
  useCrmActivity,
  useCrmActivityChain,
  useCrmFollowUpSummary,
  useReassignCrmFollowUp,
  useRescheduleCrmFollowUp,
} from '@/hooks/useCrmActivities';
import { useUsers } from '@/hooks/useUsers';
import { listSalesTeams } from '@/services/salesTeam.service';
import type { CrmActivity, CrmActivityListFilters, CrmFollowUpBucket } from '@/services/crmActivity.service';
import CrmActivityTimeline, {
  CrmFollowUpTag,
  CrmRecordLinks,
  crmPersonName,
} from '@/components/crm/activities/CrmActivityTimeline';
import { formatDate } from '@/utils/format/date';

const { Title, Text, Paragraph } = Typography;

type ViewMode = 'mine' | 'team';
type ActionKind = 'complete' | 'reschedule' | 'reassign';

const BUCKETS: { key: CrmFollowUpBucket; label: string; color?: string }[] = [
  { key: 'overdue', label: 'Overdue', color: '#cf1322' },
  { key: 'today', label: 'Due today', color: '#d46b08' },
  { key: 'this_week', label: 'This week' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'done', label: 'Done' },
];

const FollowUpTrackerPage: React.FC = () => {
  const { user } = useAuthStore();
  const canViewTeam = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.view_team'));
  const canEdit = Boolean(
    user?.is_super_admin ||
      user?.permissions?.some((p) => p === 'crm.activity.edit' || p === 'crm.activity.create')
  );
  const canReassign = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.activity.edit'));
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get('activity') || undefined;

  const [mode, setMode] = useState<ViewMode>('mine');
  const [bucket, setBucket] = useState<CrmFollowUpBucket>('overdue');
  const [owner, setOwner] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [commitmentsOnly, setCommitmentsOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [chainFor, setChainFor] = useState<string | undefined>(focusId);
  const [action, setAction] = useState<{ kind: ActionKind; row: CrmActivity } | null>(null);
  const [form] = Form.useForm();

  const { data: users = [] } = useUsers({ is_active: true });
  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(true),
    enabled: canViewTeam,
  });

  const baseFilters: CrmActivityListFilters = useMemo(
    () => ({
      follow_up_owner: mode === 'mine' ? user?.id : owner,
      sales_team_id: mode === 'team' ? teamId : undefined,
      commitments_only: commitmentsOnly || undefined,
      search: search.trim() || undefined,
      include_system: true,
      today: dayjs().format('YYYY-MM-DD'),
    }),
    [mode, user?.id, owner, teamId, commitmentsOnly, search]
  );

  const { data: counts } = useCrmFollowUpSummary(baseFilters);
  const { data: list, isLoading } = useCrmActivities({
    ...baseFilters,
    follow_up_bucket: bucket,
    page,
    limit: pageSize,
  });

  const { data: focused } = useCrmActivity(chainFor);
  const { data: chain = [], isLoading: chainLoading } = useCrmActivityChain(chainFor);

  const completeMut = useCompleteCrmFollowUp();
  const rescheduleMut = useRescheduleCrmFollowUp();
  const reassignMut = useReassignCrmFollowUp();

  useEffect(() => {
    setPage(1);
  }, [baseFilters, bucket]);

  useEffect(() => {
    if (focusId) setChainFor(focusId);
  }, [focusId]);

  const closeChain = () => {
    setChainFor(undefined);
    if (focusId) {
      searchParams.delete('activity');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const openAction = (kind: ActionKind, row: CrmActivity) => {
    form.resetFields();
    if (kind === 'complete') form.setFieldsValue({ status: 'Done' });
    if (kind === 'reschedule') {
      form.setFieldsValue({
        follow_up_date: row.follow_up_date ? dayjs(row.follow_up_date) : dayjs(),
        follow_up_time: row.follow_up_time ? dayjs(row.follow_up_time, 'HH:mm:ss') : null,
      });
    }
    if (kind === 'reassign') form.setFieldsValue({ follow_up_owner: row.follow_up_owner ?? undefined });
    setAction({ kind, row });
  };

  const submitAction = async () => {
    if (!action) return;
    const v = await form.validateFields();
    if (action.kind === 'complete') {
      await completeMut.mutateAsync({
        id: action.row.id,
        body: { status: v.status, completion_note: v.completion_note || undefined },
      });
    } else if (action.kind === 'reschedule') {
      await rescheduleMut.mutateAsync({
        id: action.row.id,
        follow_up_date: (v.follow_up_date as Dayjs).format('YYYY-MM-DD'),
        follow_up_time: v.follow_up_time ? (v.follow_up_time as Dayjs).format('HH:mm:ss') : null,
        reason: v.reason || undefined,
      });
    } else {
      await reassignMut.mutateAsync({ id: action.row.id, follow_up_owner: v.follow_up_owner });
    }
    setAction(null);
  };

  const columns: ColumnsType<CrmActivity> = [
    {
      title: 'Due',
      dataIndex: 'follow_up_date',
      width: 110,
      render: (d: string, r) => (
        <Text type={r.is_overdue ? 'danger' : undefined} strong={r.is_overdue}>
          {formatDate(d)}
        </Text>
      ),
    },
    {
      title: 'Commitment / next action',
      key: 'action',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Space size={4} wrap>
            {r.is_commitment && (
              <Tag color="purple">{r.committed_to === 'internal' ? 'Internal commitment' : 'Committed to customer'}</Tag>
            )}
            <Text strong={r.is_commitment}>{r.commitment_text || r.subject || r.activity_type_name}</Text>
          </Space>
          {r.subject && r.commitment_text && <Text type="secondary">From: {r.subject}</Text>}
        </Space>
      ),
    },
    { title: 'Record', key: 'record', width: 220, render: (_, r) => <CrmRecordLinks a={r} /> },
    {
      title: 'Owner',
      key: 'owner',
      width: 150,
      render: (_, r) => crmPersonName(r.follow_up_owner_user) || crmPersonName(r.performer),
    },
    { title: 'Status', key: 'status', width: 170, render: (_, r) => <CrmFollowUpTag a={r} /> },
    {
      title: '',
      key: 'actions',
      width: 260,
      render: (_, r) => (
        <Space size={0} wrap>
          {r.follow_up_status === 'Open' && canEdit && (
            <>
              <Button type="link" size="small" onClick={() => openAction('complete', r)}>
                Complete
              </Button>
              <Button type="link" size="small" onClick={() => openAction('reschedule', r)}>
                Reschedule
              </Button>
              {canReassign && (
                <Button type="link" size="small" onClick={() => openAction('reassign', r)}>
                  Reassign
                </Button>
              )}
            </>
          )}
          <Button type="link" size="small" onClick={() => setChainFor(r.id)}>
            History
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Follow-ups &amp; commitments
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Every follow-up date and promise made to a customer, with who owns it and whether it is overdue.
          </Paragraph>
        </div>
        {canViewTeam && (
          <Segmented
            value={mode}
            onChange={(v) => setMode(v as ViewMode)}
            options={[
              { label: 'My follow-ups', value: 'mine' },
              { label: 'Team', value: 'team' },
            ]}
          />
        )}
      </Space>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          {mode === 'team' && (
            <>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Owner"
                style={{ width: 200 }}
                value={owner}
                onChange={setOwner}
                options={users.map((u) => ({ value: u.id, label: crmPersonName(u) }))}
              />
              <Select
                allowClear
                placeholder="Sales team"
                style={{ width: 200 }}
                value={teamId}
                onChange={setTeamId}
                options={teams.map((t) => ({ value: t.id, label: t.team_name }))}
              />
            </>
          )}
          <Input.Search
            allowClear
            placeholder="Search subject, commitment, customer"
            style={{ width: 280 }}
            onSearch={setSearch}
          />
          <Checkbox checked={commitmentsOnly} onChange={(e) => setCommitmentsOnly(e.target.checked)}>
            Commitments only
          </Checkbox>
          {counts && counts.commitments_open > 0 && (
            <Tag color="purple">{counts.commitments_open} open commitments</Tag>
          )}
        </Space>
      </Card>

      <Card size="small">
        <Tabs
          activeKey={bucket}
          onChange={(k) => setBucket(k as CrmFollowUpBucket)}
          items={BUCKETS.map((b) => ({
            key: b.key,
            label: (
              <Space size={6}>
                {b.label}
                <Badge
                  count={counts?.[b.key] ?? 0}
                  showZero
                  overflowCount={999}
                  style={b.color && (counts?.[b.key] ?? 0) > 0 ? { backgroundColor: b.color } : { backgroundColor: '#8c8c8c' }}
                />
              </Space>
            ),
          }))}
        />
        <Table<CrmActivity>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={list?.items ?? []}
          rowClassName={(r) => (r.id === focusId ? 'ant-table-row-selected' : '')}
          pagination={{
            current: page,
            pageSize,
            total: list?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>

      <Drawer
        title={focused ? `Follow-up history — ${focused.subject || focused.activity_type_name}` : 'Follow-up history'}
        width={560}
        open={!!chainFor}
        onClose={closeChain}
        extra={
          focused?.follow_up_status === 'Open' && canEdit ? (
            <Space>
              <Button size="small" onClick={() => openAction('complete', focused)}>
                Complete
              </Button>
              <Button size="small" onClick={() => openAction('reschedule', focused)}>
                Reschedule
              </Button>
            </Space>
          ) : null
        }
      >
        {focused && (
          <div style={{ marginBottom: 12 }}>
            <CrmRecordLinks a={focused} />
          </div>
        )}
        <Card size="small" loading={chainLoading} bordered={false}>
          <CrmActivityTimeline activities={chain} hideSystemByDefault={false} />
        </Card>
      </Drawer>

      <Modal
        open={!!action}
        title={
          action?.kind === 'complete'
            ? 'Close follow-up'
            : action?.kind === 'reschedule'
              ? 'Reschedule follow-up'
              : 'Reassign follow-up'
        }
        okText="Save"
        onOk={submitAction}
        onCancel={() => setAction(null)}
        confirmLoading={completeMut.isPending || rescheduleMut.isPending || reassignMut.isPending}
        destroyOnClose
      >
        {action && (
          <Paragraph type="secondary">
            {action.row.commitment_text || action.row.subject || action.row.activity_type_name} · due{' '}
            {formatDate(action.row.follow_up_date)}
          </Paragraph>
        )}
        <Form form={form} layout="vertical">
          {action?.kind === 'complete' && (
            <>
              <Form.Item name="status" label="Result">
                <Radio.Group
                  options={[
                    { label: 'Done', value: 'Done' },
                    { label: 'Cancelled (no longer needed)', value: 'Cancelled' },
                  ]}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(a, b) => a.status !== b.status}>
                {({ getFieldValue }) => (
                  <Form.Item
                    name="completion_note"
                    label="Note"
                    rules={
                      getFieldValue('status') === 'Cancelled'
                        ? [{ required: true, message: 'Say why the follow-up is cancelled' }]
                        : []
                    }
                  >
                    <Input.TextArea rows={3} placeholder="What happened?" />
                  </Form.Item>
                )}
              </Form.Item>
            </>
          )}
          {action?.kind === 'reschedule' && (
            <>
              <Form.Item name="follow_up_date" label="New due date" rules={[{ required: true }]}>
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="follow_up_time" label="Time" tooltip="Leave blank for an all-day follow-up">
                <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={15} />
              </Form.Item>
              <Form.Item name="reason" label="Reason">
                <Input.TextArea rows={2} />
              </Form.Item>
            </>
          )}
          {action?.kind === 'reassign' && (
            <Form.Item name="follow_up_owner" label="New owner" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={users.map((u) => ({ value: u.id, label: crmPersonName(u) }))}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default FollowUpTrackerPage;
